import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { BehaviorSubject } from 'rxjs';

import { InputComponent } from '@shared/components/input/input.component';
import { ModalComponent } from '@shared/components/modals/modal/modal.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { DepartmentType, RoleType } from '@shared/services/gql.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { UserModel } from '@shared/store/trial-users/trial-user.store';
import { Utils } from '@shared/utils/utils';

export interface UserDialogSubmit {
  department: DepartmentType[];
  email: string;
  family_name: string;
  given_name: string;
  permissions: string;
  role: RoleType;
  title: string;
  vendor: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    NgSelectModule,
    FormErrorDirective,
    ModalComponent,
  ],
  selector: 'aux-user-dialog',
  styles: [],
  templateUrl: './user-dialog.component.html',
})
export class UserDialogComponent {
  vendorsQuery = inject(OrganizationQuery);

  private formBuilder = inject(UntypedFormBuilder);

  addUserForm = this.formBuilder.group({
    approvers: '',
    department: null,
    email: '',
    family_name: '',
    given_name: '',
    permissions: '',
    role: '',
    title: '',
  });
  departmentOptions = Object.entries(Utils.DEPARTMENT_OPTIONS).map(([key, label]) => ({
    key,
    label,
  }));
  isAuxiliusSupport$ = new BehaviorSubject<boolean>(false);
  mode: 'add' | 'edit' = 'edit';
  ref = inject<
    CustomOverlayRef<
      UserDialogSubmit,
      {
        formValue: UserModel;
        mode: 'add' | 'edit';
      }
    >
  >(CustomOverlayRef);
  userOptions = [
    { label: 'Admin', value: 'ROLE_ADMIN' },
    { label: 'User', value: 'ROLE_USER' },
  ];

  constructor() {
    if (this.ref.data?.mode) {
      this.mode = this.ref.data.mode;
    }

    if (this.ref.data?.formValue) {
      this.addUserForm.patchValue(this.ref.data?.formValue);
    }

    this.getFc('email')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((email: string) => {
        const isAuxiliusEmail = email.includes('@auxili.us');

        this.isAuxiliusSupport$.next(isAuxiliusEmail);
      });
  }

  getFc(str: string) {
    return this.addUserForm.get(str);
  }

  onCancel() {
    this.ref.close();
  }

  onSubmit() {
    if (this.addUserForm.valid) {
      this.ref.close(this.addUserForm.value);
    }
  }
}
